import { useState, useEffect } from "react";
import { API_BASE_URL } from "../lib/authFetch";

interface EmailVerificationModalProps {
  open: boolean;
  email: string;
  onSuccess: () => void;
  onClose: () => void;
}

export default function EmailVerificationModal({ open, email, onSuccess, onClose }: EmailVerificationModalProps) {
  const [code, setCode] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState("");
  const [resendMsg, setResendMsg] = useState("");

  useEffect(() => {
    if (open) setCode("");
  }, [open]);

  const handleVerify = async () => {
    setIsVerifying(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE_URL}/verify-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, code }),
      });
      if (res.ok) {
        onSuccess();
      } else {
        const data = await res.json();
        let errorMsg = "Código inválido";
        if (typeof data.detail === "string") {
          errorMsg = data.detail;
        } else if (Array.isArray(data.detail)) {
          // FastAPI pode retornar um array de erros
          errorMsg = data.detail.map(e => e.msg).join(" | ");
        } else if (typeof data.detail === "object" && data.detail !== null) {
          errorMsg = JSON.stringify(data.detail);
        }
        setError(errorMsg);
      }
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("Erro inesperado ao conectar ao servidor");
      }
    }
    setIsVerifying(false);
  };

  const handleResend = async () => {
    setError("");
    setResendMsg("");
    setIsVerifying(true);
    try {
      const res = await fetch(`${API_BASE_URL}/resend-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (res.ok) {
        setResendMsg("Código reenviado! Verifique seu e-mail.");
      } else {
        const data = await res.json();
        let errorMsg = "Erro ao reenviar código";
        if (typeof data.detail === "string") {
          errorMsg = data.detail;
        } else if (Array.isArray(data.detail)) {
          // FastAPI pode retornar um array de erros
          errorMsg = data.detail.map(e => e.msg).join(" | ");
        } else if (typeof data.detail === "object" && data.detail !== null) {
          errorMsg = JSON.stringify(data.detail);
        }
        setError(errorMsg);
      }
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("Erro inesperado ao conectar ao servidor");
      }
    }
    setIsVerifying(false);
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50">
      <div className="bg-protec-dark-light p-8 rounded-lg shadow-lg w-full max-w-md flex flex-col items-center">
        <h2 className="text-xl font-bold mb-2 text-protec-gold">Verifique seu e-mail</h2>
        <p className="text-gray-300 mb-4 text-center">Enviamos um código para <b>{email}</b>. Digite abaixo:</p>
        <input
          type="text"
          value={code}
          onChange={e => setCode(e.target.value)}
          placeholder="Código de 6 dígitos"
          className="mb-2 px-4 py-2 rounded border border-protec-gold/30 bg-protec-dark text-white w-full text-center"
        />
        <button
          onClick={handleVerify}
          disabled={isVerifying}
          className="w-full bg-gradient-gold text-black font-semibold py-2 rounded mb-2"
        >
          {isVerifying ? "Verificando..." : "Verificar"}
        </button>
        <button
          onClick={handleResend}
          disabled={isVerifying}
          className="w-full bg-protec-dark border border-protec-gold text-protec-gold font-semibold py-2 rounded mb-2"
        >
          Reenviar código
        </button>
        {resendMsg && <div className="text-green-400 text-sm mb-2">{resendMsg}</div>}
        {error && <div className="text-red-400 text-sm mb-2">{error}</div>}
        <button onClick={onClose} className="text-protec-gold text-xs underline">Cancelar</button>
      </div>
    </div>
  );
} 